import { useReactiveVar } from '@apollo/client'
import { WorkspaceProjectElementNode } from 'api/graphQL/reactiveVars/element'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import { processEnv } from 'components/configuration/environmentVariables'
import type { DataNode } from 'components/WorkspaceEditor/types/types'
import { isManufacturerOrBODPill } from 'components/WorkspaceEditor/utils'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useCallback, useMemo } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { BP_NODE_KEYS } from '../constants'
import { useApplyBPFromConfig } from './useApplyBPFromConfig'
import { useBestPracticesMenu } from './useBestPracticesMenu'
import { useShouldRenderBPIcon } from './useShouldRenderBPIcon'

interface BPNodeInteractionsParams {
    node: DataNode
    projectId: string | undefined
    isManufacturerOrBODNode: boolean
    isProductLibraryButtonNode: boolean
    isOMAddManufacturerNode: boolean
}

interface BPNodeInteractionsResult {
    showBestPracticesIcons: boolean
    isBPManagementEnabled: boolean
    handleRightClick: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
    handleDoubleClick: (event: React.MouseEvent<HTMLSpanElement>) => void
    isBPMenuOpen: boolean
    bpAnchorElement: Element | null
    closeBPMenu: () => void
}

export const useBPNodeInteractions = ({
    node,
    projectId,
    isManufacturerOrBODNode,
    isProductLibraryButtonNode,
    isOMAddManufacturerNode,
}: BPNodeInteractionsParams): BPNodeInteractionsResult => {
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const showBPSetting = useReactiveVar(showBestPracticeSetting)
    const { isBPMenuOpen, bpAnchorElement, openBPMenu, closeBPMenu } = useBestPracticesMenu()
    const applyBPFromConfig = useApplyBPFromConfig()
    const shouldRenderBPIcon = useShouldRenderBPIcon(node)

    const isBPManagementEnabled = isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace

    const isPTNotAdded = selectedProjectElementNode?.isInProject === false
    const isFromPPLTemplate =
        (selectedProjectElementNode as WorkspaceProjectElementNode)?.isFromPPLTemplate ?? false

    const showBestPracticesFeatureInNewWorkspace = useMemo(
        () =>
            processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace === '1',
        [processEnv]
    )

    const shouldDisableBPMenuForUserAddedContent = useMemo(
        () =>
            processEnv.REACT_APP_EnableSmartProjectCopy !== '1' &&
            (node.isUserAdded ?? false),
        [processEnv, node.isUserAdded]
    )

    const showBestPracticesIcons = useMemo(
        () =>
            shouldRenderBPIcon &&
            !isManufacturerOrBODNode &&
            !isProductLibraryButtonNode &&
            !isOMAddManufacturerNode &&
            showBestPracticesFeatureInNewWorkspace,
        [
            shouldRenderBPIcon,
            isManufacturerOrBODNode,
            isProductLibraryButtonNode,
            isOMAddManufacturerNode,
            showBestPracticesFeatureInNewWorkspace,
        ]
    )

    const handleRightClick = useCallback(
        (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            event.preventDefault()
            event.stopPropagation()

            const contentItemId = node.key
            const isManufacturerOrBODNodeLocal = isManufacturerOrBODPill(node)
            const isProductLibraryButtonNodeLocal =
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1' &&
                (String(contentItemId).startsWith('product-library-btn') ||
                    String(contentItemId).startsWith('om-add-mfr-btn'))

            if (
                isManufacturerOrBODNodeLocal ||
                isProductLibraryButtonNodeLocal ||
                !contentItemId ||
                !projectId ||
                !showBestPracticesFeatureInNewWorkspace ||
                !showBPSetting ||
                shouldDisableBPMenuForUserAddedContent ||
                isFromPPLTemplate ||
                isCurrentlyOnOMWorkspace ||
                isPTNotAdded
            ) {
                return
            }

            if (event.type === 'contextmenu') {
                openBPMenu({
                    anchorElement: event.currentTarget,
                    contentItemId: contentItemId,
                    contentItemType: node.type || '',
                    projectId: projectId,
                    elementId: undefined,
                })
            }
        },
        [
            node,
            projectId,
            openBPMenu,
            showBestPracticesFeatureInNewWorkspace,
            shouldDisableBPMenuForUserAddedContent,
            showBPSetting,
            isFromPPLTemplate,
            isCurrentlyOnOMWorkspace,
            isPTNotAdded,
        ]
    )

    const handleDoubleClick = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
            if (!isBPManagementEnabled) return

            event.preventDefault()
            event.stopPropagation()

            const contentItemId = node.key
            const isManufacturerOrBODPillNode = isManufacturerOrBODPill(node)
            const isBPProductLibraryButtonNode =
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1' &&
                contentItemId === BP_NODE_KEYS.PRODUCT_LIBRARY_BUTTON

            if (
                isManufacturerOrBODPillNode ||
                isBPProductLibraryButtonNode ||
                !contentItemId ||
                !projectId ||
                !showBPSetting ||
                isPTNotAdded
            ) {
                return
            }

            applyBPFromConfig({
                anchorElement: event.currentTarget,
                contentItemId: contentItemId,
                contentItemType: node.type || '',
                projectId: projectId,
                elementId: undefined,
            })
        },
        [
            node,
            projectId,
            showBPSetting,
            applyBPFromConfig,
            isBPManagementEnabled,
            isPTNotAdded,
        ]
    )

    return {
        showBestPracticesIcons,
        isBPManagementEnabled,
        handleRightClick,
        handleDoubleClick,
        isBPMenuOpen,
        bpAnchorElement,
        closeBPMenu,
    }
}
